import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiFetch, apiFetchCached } from '../../utils/apiClient';
import './SearchCollectibles.css';
import MsvBrowseShell from '../../components/MsvBrowseShell/MsvBrowseShell';
import VaultCollectibleFilterForm from '../../components/VaultCollectibleFilterForm/VaultCollectibleFilterForm';
import { getVaultCollectibleBrowseExtras } from '../../components/VaultCollectibleBrowseExtras/VaultCollectibleBrowseExtras';
import InfiniteFeed, { FeedSkeleton } from '../../components/InfiniteFeed/InfiniteFeed';
import WishlistHeartButton from '../../components/WishlistHeartButton/WishlistHeartButton';
import { resolveImageSrc } from '../../utils/imageUtils';
import { toTitleCase } from '../../utils/textUtils';
import { isCollectibleListedForSale } from '../../utils/marketplaceForSale';
import { DIECASTS_MAIN_CATEGORY } from '../../constants/dropdownIdPairs';
import { buildCollectibleCategorySearch, buildCollectibleSearchParams, navigateVaultSearch } from '../../utils/vaultBrowseSearch';
import {
    ALL_ITEMS_MAIN_CATEGORY,
    DEFAULT_RACE_USED_MAIN_CATEGORY,
    parseVaultMainCategoryFromUrl,
    restoreVaultMainCategory,
    resolveMainCategoryForApi,
    resolveVaultSectionEyebrow,
    resolveVaultSectionTitle,
} from '../../utils/vaultBrowseCategories';
import {
    isValidCollectibleSort,
    normalizeCollectibleSort,
    resolveCollectibleRequestSort,
} from '../../utils/vaultCollectibleSort';
import {
    resolveDisplayedVaultResultCount,
    resolveSitewideBrowseCategoryCount,
} from '../../utils/userCollectionCounts';

const SearchCollectibles = ({ vaultMode = 'raceUsed' }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const isDiecastsMode = vaultMode === 'diecasts';
    const SESSION_STATE_KEY = isDiecastsMode ? 'diecastsSearchState' : 'collectiblesSearchState';
    const SESSION_RESTORE_FLAG = isDiecastsMode ? 'diecastsPendingRestore' : 'collectiblesPendingRestore';
    const searchBasePath = isDiecastsMode ? '/searchDiecasts' : '/searchCollectibles';
    const sectionTitleFallback = isDiecastsMode ? 'Diecasts' : 'Race used';

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 24;
    const [sortBy, setSortBy] = useState('recent');
    const [, setNextPageToken] = useState(null);
    const [hasMore, setHasMore] = useState(true);

    const [searchStarted, setSearchStarted] = useState(false);
    const [initialLoad, setInitialLoad] = useState(true);
    const [paginationTokens, setPaginationTokens] = useState({ 1: null });

    const isFirstVisit = useRef(true);

    // State to handle form inputs
    const [formData, setFormData] = useState({
        driverName: '',
        series: '',
        mainCategory: isDiecastsMode ? DIECASTS_MAIN_CATEGORY : DEFAULT_RACE_USED_MAIN_CATEGORY,
        subCategory: '',
        subSubCategory: '',
        subSubSubCategory: ''
    });

    const [searchResults, setSearchResults] = useState([]);

    const [loadingSearch, setLoadingSearch] = useState(false);
    const [, setErrors] = useState({});
    const [, setSuccessMessage] = useState('');
    const [, setApiErrorMessage] = useState('');

    const [sitewideStats, setSitewideStats] = useState(null);
    const [, setTotalPages] = useState(0);

    const categoryTotal = resolveSitewideBrowseCategoryCount(
        sitewideStats,
        formData.mainCategory,
        isDiecastsMode
    );
    // Driver / series / subcategory filters have no sitewide counter — show loaded count.
    const filtersActive = !!(
        formData.driverName
        || formData.series
        || formData.subCategory
        || formData.subSubCategory
        || formData.subSubSubCategory
    );
    const displayedResultCount = resolveDisplayedVaultResultCount({
        categoryTotal,
        searchResultsLength: searchResults.length,
        hasMore,
        loading: loadingSearch,
        initialLoad,
        filtersActive,
    });

    useEffect(() => {
        // Only restore scroll/page when returning from an item detail view.
        // A normal visit (sidebar, category switch) must start at page 1 so newest
        // uploads are not skipped by a stale pagination token.
        const shouldRestore = sessionStorage.getItem(SESSION_RESTORE_FLAG) === 'true';
        sessionStorage.removeItem(SESSION_RESTORE_FLAG);

        if (shouldRestore) {
            const savedState = sessionStorage.getItem(SESSION_STATE_KEY);
            if (savedState) {
                try {
                    const {
                        formData: savedFormData,
                        paginationTokens: _savedTokens,
                        selectedFilters,
                        sortBy: savedSortBy
                    } = JSON.parse(savedState);

                    const restoredForm = {
                        driverName: savedFormData?.driverName
                            ?? selectedFilters?.selectedDriver?.fullName
                            ?? '',
                        series: savedFormData?.series
                            ?? selectedFilters?.selectedSeries?.seriesName
                            ?? '',
                        mainCategory: savedFormData?.mainCategory
                            ?? selectedFilters?.selectedMainCategory?.name
                            ?? '',
                        subCategory: savedFormData?.subCategory
                            ?? selectedFilters?.selectedDiecastSubCategory?.name
                            ?? selectedFilters?.selectedRaceUsedSubCategory?.name
                            ?? selectedFilters?.selectedTradingCardYearCategory?.name
                            ?? selectedFilters?.selectedHeroCardYearCategory?.name
                            ?? selectedFilters?.selectedProgramTicketYearCategory?.name
                            ?? '',
                        subSubCategory: savedFormData?.subSubCategory
                            ?? selectedFilters?.selectedDiecastYearCategory?.name
                            ?? selectedFilters?.selectedSheetMetalSubCategory?.name
                            ?? selectedFilters?.selectedTeamIssuedSubCategory?.name
                            ?? selectedFilters?.selectedDriverWornSubCategory?.name
                            ?? selectedFilters?.selectedTradingCardBrandCategory?.name
                            ?? '',
                        subSubSubCategory: savedFormData?.subSubSubCategory
                            ?? selectedFilters?.selectedDiecastVersionCategory?.name
                            ?? selectedFilters?.selectedTradingCardTypeCategory?.name
                            ?? '',
                    };

                    setFormData(isDiecastsMode
                        ? { ...restoredForm, mainCategory: DIECASTS_MAIN_CATEGORY }
                        : { ...restoredForm, mainCategory: restoreVaultMainCategory(restoredForm.mainCategory) });
                    // Always re-query from the start so latest items are included; keep filters/sort.
                    setCurrentPage(1);
                    setPaginationTokens({ 1: null });
                    if (isValidCollectibleSort(savedSortBy)) {
                        setSortBy(savedSortBy);
                    }
                } catch (error) {
                    console.error('Error restoring collectibles search state:', error);
                    sessionStorage.removeItem(SESSION_STATE_KEY);
                }
            }
        } else {
            sessionStorage.removeItem(SESSION_STATE_KEY);
            if (isDiecastsMode) {
                setFormData((prev) => ({ ...prev, mainCategory: DIECASTS_MAIN_CATEGORY }));
            }
            setCurrentPage(1);
            setPaginationTokens({ 1: null });
        }

        // Always run a search on open - results are not stored in session state.
        isFirstVisit.current = true;
        setSearchStarted(true);
    }, [SESSION_STATE_KEY, SESSION_RESTORE_FLAG, isDiecastsMode]);

    // Fetch sitewide category totals for the results counter.
    useEffect(() => {
        const fetchTotalCount = async () => {
            try {
                const response = await apiFetchCached('/Collectibles/GetHomePageData', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' }
                });
                
                if (response.ok) {
                    const data = await response.json();
                    const sitewide = data.sitewideDataResultDto || null;
                    setSitewideStats(sitewide);
                    const totalCount = sitewide?.numberOfCollectibles || 0;
                    setTotalPages(Math.ceil(totalCount / itemsPerPage));
                }
            } catch (err) {
                console.error('Failed to fetch total collectibles count:', err);
            }
        };

        fetchTotalCount();
    }, []);

    useEffect(() => {
        // On first mount with no query string, leave session-restore / default search alone.
        if (!location.search && isFirstVisit.current) {
            return;
        }

        const params = new URLSearchParams(location.search);
        const driver = params.get('driver') || '';
        const series = params.get('series') || '';
        const mainCategory = isDiecastsMode
            ? DIECASTS_MAIN_CATEGORY
            : parseVaultMainCategoryFromUrl(params);
        const subCategory = params.get('subCategory') || '';
        const subSubCategory = params.get('subSubCategory') || '';
        const subSubSubCategory = params.get('subSubSubCategory') || '';
        const page = parseInt(params.get('page') || '1', 10);

        setFormData({
            driverName: driver,
            series,
            mainCategory,
            subCategory,
            subSubCategory,
            subSubSubCategory
        });
        const sortParam = params.get('sort');
        if (isValidCollectibleSort(sortParam)) {
            setSortBy(sortParam);
        }
        setCurrentPage(Number.isFinite(page) && page > 0 ? page : 1);
        setSearchStarted(true);
    }, [location.search, isDiecastsMode]);

    useEffect(() => {
        if (!searchStarted) return;

        let cancelled = false;

        const fetchData = async () => {
            setLoadingSearch(true);

            // Prefer sort from the URL so a sort change never races with React state.
            const urlSort = new URLSearchParams(location.search).get('sort');
            const requestSort = resolveCollectibleRequestSort(urlSort, sortBy);

            try {
                // Don't cache search - sort/filter changes must always hit the API.
                const response = await apiFetch('/Collectibles/SearchCollectibles', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: {
                        DriverName: formData.driverName,
                        Series: formData.series,
                        MainCategory: isDiecastsMode
                            ? DIECASTS_MAIN_CATEGORY
                            : resolveMainCategoryForApi(formData.mainCategory),
                        SubCategory: formData.subCategory,
                        SubSubCategory: formData.subSubCategory,
                        SubSubSubCategory: formData.subSubSubCategory,
                        // All items = no MainCategory and no ExcludeMainCategory.
                        // Race used / Diecasts / Trading cards / etc. are MainCategory filters only.
                        ExcludeMainCategory: null,
                        SortBy: requestSort,
                        PageSize: 24,
                        PaginationToken: paginationTokens[currentPage] || null
                    }
                });

                if (cancelled) return;

                const data = await response.json();

                const resultsWithBase64 = (data.collectibleSearchResultDtos || []).map(item => ({
                    ...item,
                    imageUrl: resolveImageSrc(item)
                }));

                setPaginationTokens(prev => ({ ...prev, [currentPage + 1]: data.nextPageToken }));
                setNextPageToken(data.nextPageToken || null);
                setHasMore(!!data.nextPageToken);
                setSearchResults(prev => currentPage > 1 ? [...prev, ...resultsWithBase64] : resultsWithBase64);
            } catch (err) {
                console.error(err);
            } finally {
                if (!cancelled) {
                    setLoadingSearch(false);
                    setInitialLoad(false);
                    setSearchStarted(false);
                    isFirstVisit.current = false;
                }
            }
        };
        fetchData();

        return () => { cancelled = true; };
    }, [currentPage, formData, sortBy, searchStarted, location.search]);

    useEffect(() => {
        // Only jump to top on a fresh search - not when InfiniteFeed appends the next page
        if (!initialLoad && searchResults.length > 0 && currentPage === 1) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [searchResults, initialLoad, currentPage]);

    const validateForm = () => {
        const newErrors = {};

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleFiltersApply = (nextForm) => {
        sessionStorage.removeItem(SESSION_STATE_KEY);
        setPaginationTokens({ 1: null });
        setSearchResults([]);
        setHasMore(true);
        setSuccessMessage('');
        setApiErrorMessage('');
        isFirstVisit.current = false;

        const { isDiecastsMode: nextDiecastsMode, params } = buildCollectibleCategorySearch(nextForm, {
            isDiecastsMode,
            sortBy,
        });
        const basePath = nextDiecastsMode ? '/searchDiecasts' : '/searchCollectibles';

        navigateVaultSearch(navigate, location, {
            basePath,
            params,
            onSameUrl: () => {
                setCurrentPage(1);
                setSearchStarted(true);
            },
        });
    };

    const handleSortChange = (nextValue) => {
        const nextSort = normalizeCollectibleSort(nextValue);
        sessionStorage.removeItem(SESSION_STATE_KEY);
        setSortBy(nextSort);
        setPaginationTokens({ 1: null });
        setCurrentPage(1);
        setSearchResults([]);
        setHasMore(true);
        setSearchStarted(true);
        isFirstVisit.current = false;

        const searchParams = new URLSearchParams({
            driver: formData.driverName || '',
            series: formData.series || '',
            mainCategory: isDiecastsMode ? DIECASTS_MAIN_CATEGORY : (formData.mainCategory || ''),
            subCategory: formData.subCategory || '',
            subSubCategory: formData.subSubCategory || '',
            subSubSubCategory: formData.subSubSubCategory || '',
            sort: nextSort,
            page: '1'
        });

        const nextQuery = `?${searchParams.toString()}`;
        if (location.search === nextQuery) {
            return;
        }

        navigate(`${searchBasePath}${nextQuery}`);
    };

    const handleClearFilters = () => {
        sessionStorage.removeItem(SESSION_STATE_KEY);
        setSortBy('recent');
        setPaginationTokens({ 1: null });
        setCurrentPage(1);
        setSearchResults([]);
        setHasMore(true);
        setSuccessMessage('');
        setApiErrorMessage('');
        isFirstVisit.current = false;

        // Clear category + filters back to All items (exits Diecasts mode when needed).
        handleFiltersApply({
            driverName: '',
            series: '',
            mainCategory: ALL_ITEMS_MAIN_CATEGORY,
            subCategory: '',
            subSubCategory: '',
            subSubSubCategory: '',
        });
    };

    const handleNavigateToItem = (item) => {
        const stateToSave = {
            formData,
            currentPage,
            paginationTokens,
            searchStarted,
            sortBy,
        };

        sessionStorage.setItem(SESSION_RESTORE_FLAG, 'true');
        sessionStorage.setItem(SESSION_STATE_KEY, JSON.stringify(stateToSave));
        window.location.href = `/collectionItem/${item.collectionItemId}`;
    };

    const browseExtras = getVaultCollectibleBrowseExtras({
        isDiecastsMode,
        formData,
        onFiltersApply: handleFiltersApply,
        onClearFilters: handleClearFilters,
    });

    return (
        <MsvBrowseShell
            className="search-collectibles-page"
            eyebrow="Browse"
            title="The Vault"
            hubVariant="vault"
            sectionEyebrow={resolveVaultSectionEyebrow(isDiecastsMode)}
            sectionTitle={resolveVaultSectionTitle(formData, isDiecastsMode, formData.subCategory) || sectionTitleFallback}
            resultCount={displayedResultCount}
            sidebarBelowNav={browseExtras.sidebarBelowNav}
            belowToolbar={browseExtras.belowToolbar}
            toolbar={(
                <VaultCollectibleFilterForm
                    isDiecastsMode={isDiecastsMode}
                    formData={formData}
                    onFiltersApply={handleFiltersApply}
                    sortBy={sortBy}
                    onSortChange={handleSortChange}
                    onClearFilters={handleClearFilters}
                />
            )}
        >
                {loadingSearch && searchResults.length === 0 ? (
                    <FeedSkeleton />
                ) : (
                    <InfiniteFeed
                        items={searchResults}
                        getKey={(item, index) => item.collectionItemId || index}
                        hasMore={hasMore}
                        loading={loadingSearch && searchResults.length === 0}
                        loadingMore={loadingSearch && searchResults.length > 0}
                        emptyMessage="No Collectibles Found"
                        onLoadMore={() => {
                            if (!hasMore || loadingSearch) return;
                            setSearchStarted(true);
                            setCurrentPage(prev => prev + 1);
                        }}
                        renderItem={(item) => (
                            <div className="msv-item-card">
                                <div className="msv-item-card-image">
                                    <img src={item.imageUrl} alt={item.title} loading="lazy" decoding="async" />
                                    <WishlistHeartButton itemType="collectible" item={item} />
                                    {isCollectibleListedForSale(item) && (
                                        <span className="msv-for-sale-badge">
                                            For Sale
                                            {Number.isFinite(Number(item.marketplacePrice))
                                                ? ` · $${Number(item.marketplacePrice).toFixed(0)}`
                                                : ''}
                                        </span>
                                    )}
                                </div>
                                <div className="msv-item-card-body">
                                    <h4 className="msv-item-card-title">{toTitleCase(item.title)}</h4>
                                    <button type="button" onClick={() => handleNavigateToItem(item)} className="msv-item-card-btn">
                                        View
                                    </button>
                                </div>
                            </div>
                        )}
                    />
                )}
        </MsvBrowseShell>
    );
};

export default SearchCollectibles;